import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";

import { auth, db } from "../../firebase";
import { doc, collection, getDoc, getDocs, setDoc, addDoc, deleteDoc } from "firebase/firestore";

// 새롭게 추가된 import (파일 업로드 및 이미지 압축)
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import imageCompression from 'browser-image-compression';

import { saveUserLog } from './LogandUtils'; // 경로에 맞게 수정

import SEO from '../SEO';
import Popup from '../Popup';
import Loading from "../Loading";
import MyPageMenu from './MyPageMenu';
import MusicardSourcePopup from "./MusicardSourcePopup";

import styleClasses from "./css/MyPage.module.scss";

function PositionsDetail() {
  // URL parameter: positionId가 "new"면 추가, 아니면 수정 모드
  const { positionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isSubmitting    = useRef(false);

  const [userID, setUserID]                     = useState('');
  // 포지션 이름과 스킬 목록 상태
  const [positionName, setPositionName]         = useState("");
  const [skills, setSkills]                     = useState([]); // 각 스킬: { id, name, icon, level, link }
  const [isLoading, setIsLoading]               = useState(true);
  const [skillIndex, setSkillIndex]             = useState(0);

  const [buttonLoading, setButtonLoading]       = useState(false);
  const [hasUpdatedSource, setHasUpdatedSource] = useState(false);

  const [openPopup, setOpenPopup]               = useState(false);
  const [popupTitle, setPopupTitle]             = useState('');
  const [popupButtonText, setPopupButtonText]   = useState('');
  const [customFunction, setCustomFunction]     = useState('');

  const [openSourcePopup, setOpenSourcePopup]   = useState(false);

  // 신규: 파일 업로드 진행 상태 (업로드 중인 스킬 인덱스)
  const [uploadingSkillIndex, setUploadingSkillIndex] = useState(null);

  // 수정 모드일 경우 Firestore에서 데이터 가져오기
  const fetchPositions = useCallback(async () => {
    if (positionId !== "new") {
      const fetchPositionData = async () => {
        setIsLoading(true);
        try {
          // /users_data/[UID]/positions/{positionId} 문서 조회
          const posDocRef = doc(db, "users_data", userID, "positions", positionId);
          const posDocSnap = await getDoc(posDocRef);
          if (posDocSnap.exists()) {
            // 포지션 문서에 별도 필드가 없다면, 문서 ID가 포지션 이름이 됩니다.
            setPositionName(positionId);
            // skills 하위컬렉션 조회
            const skillsRef = collection(db, "users_data", userID, "positions", positionId, "skills");
            const skillsSnapshot = await getDocs(skillsRef);
            const fetchedSkills = skillsSnapshot.docs.map((skillDoc) => ({
              id: skillDoc.id,
              ...skillDoc.data(),
            }));
            // 스킬명을 기준으로 정렬 (필요에 따라 다른 기준으로 정렬 가능)
            fetchedSkills.sort((a, b) => a.name.localeCompare(b.name));
            setSkills(fetchedSkills);
          } else {
            setOpenPopup(true);
            setPopupTitle('포지션 데이터를 찾을 수 없습니다.');
            setPopupButtonText('');
            setCustomFunction('');
          }
        } catch (error) {
          console.error(error);
        }
        setIsLoading(false);
      };
      fetchPositionData();
    } else {
      // 새 포지션 추가: 초기 상태 비움
      setPositionName("");
      setSkills([]);
      setIsLoading(false);
    }
  }, [positionId, userID]);

  // 스킬 항목 변경 핸들러
  const handleSkillChange = (index, field, value) => {
    setSkills((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // 신규: 스킬 이미지 파일 업로드 핸들러  
  const handleSkillFileUpload = async (event, index) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      setUploadingSkillIndex(index);

      const options = {
        maxWidthOrHeight: 500,
        useWebWorker: true,
        maxSizeMB: 0.5,          // 최대 0.5MB
        fileType: 'image/jpeg',
        initialQuality: 0.8,
      };
      const compressedFile = await imageCompression(file, options);
      
      const storage = getStorage();
      // 고유 파일 경로 구성 (userID, positionName, 타임스탬프 등을 이용)
      const filePath = `users/${userID}/positions/${positionName || 'new'}/skills/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, filePath);
      const snapshot = await uploadBytes(storageRef, compressedFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // 해당 스킬의 icon 필드를 다운로드 URL로 업데이트
      handleSkillChange(index, "icon", downloadURL);
      setUploadingSkillIndex(null);
    } catch (error) {
      console.error("파일 업로드 오류:", error);
      setUploadingSkillIndex(null);
      setOpenPopup(true);
      setPopupTitle("파일 업로드 중 오류가 발생했습니다.");
      setPopupButtonText('');
      setCustomFunction('');
    }
  };

  // SourceSelector에서 선택한 데이터를 받아 처리
  useEffect(() => {
    // location.state와 skills 배열이 준비되어 있고, skillIndex가 유효할 때만 처리
    if (
      !hasUpdatedSource &&
      location.state &&
      location.state.selectedSource &&
      typeof location.state.skillIndex === "number" &&
      skills.length > location.state.skillIndex
    ) {
      const { selectedSource, skillIndex } = location.state;

      let iconValue = "";
      let linkValue = "";
    
      if (selectedSource.videoId) {
        const videoId = selectedSource.videoId;
        linkValue = `https://www.youtube.com/watch?v=${videoId}`;
        iconValue = `https://img.youtube.com/vi/${videoId}/0.jpg`;
      } else {
        if (selectedSource.cover) {
        iconValue = selectedSource.cover;
        } else if (selectedSource.photo) {
        iconValue = selectedSource.photo;
        } else if (selectedSource.icon) {
        iconValue = selectedSource.icon;
        }
        
        if (selectedSource.link) {
        linkValue = selectedSource.link;
        } else if (selectedSource.url) {
        linkValue = selectedSource.url;
        }
      }
      
      //console.log(`Updating skill index ${skillIndex} with icon: ${iconValue}, link: ${linkValue}`);
      
      // 스킬 업데이트
      handleSkillChange(skillIndex, "icon", iconValue);
      handleSkillChange(skillIndex, "link", linkValue);
      
      // 한 번 처리되었음을 표시하고, location.state를 초기화
      setHasUpdatedSource(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.state, hasUpdatedSource, skills]);  

  // 스킬 항목 추가
  const addSkill = () => {
    setSkills((prev) => [...prev, { name: "", icon: "", level: "Beginner", link: "" }]);
  };

  // 스킬 항목 삭제
  const removeSkill = (index) => {
    setSkills((prev) => prev.filter((_, idx) => idx !== index));
  };


  // 🔹 인덱스 설정 및 소스 팝업 오픈
  const clickRetrieveAlbum = (index) => {
    setSkillIndex(index);
    setOpenSourcePopup(true);
  }

  // 🔹 소스 팝업 - 앨범 선택 (트랙)
  const handleTrackSelect = (newID, newThumbnail) => {
    setSkills((prev) =>
      prev.map((skill, index) =>
        index === skillIndex ? { ...skill, icon: newThumbnail, link: newID } : skill
      )
    );
    setOpenSourcePopup(false);
  };

  // 🔹 소스 팝업 - 액티비티 선택
  const handleActivitySelect = (activity) => {
    const isYouTube = activity.channelType === "YouTube";
    
    const icon = isYouTube
      ? `https://img.youtube.com/vi/${activity.videoId}/0.jpg`
      : activity.photo;
  
    const link = isYouTube
      ? `https://www.youtube.com/watch?v=${activity.videoId}`
      : activity.link ?? '';
  
    setSkills((prev) =>
      prev.map((skill, index) =>
        index === skillIndex
          ? { ...skill, icon, link }
          : skill
      )
    );
  
    setOpenSourcePopup(false);
  };  

  // 🔹 저장 핸들러 (추가 또는 수정)
  const handleSave = async () => {
    let emptySkill  = false;
    let targetIndex  = 0;
    if (skills.length === 0) {
      emptySkill = true;
    } else {
      for (let i = 0; i < skills.length ; i++) {
        if (skills[i].name === '') {
          emptySkill = true;
          targetIndex = i + 1;
          break;
        }
      }
    }

    if (!positionName.trim()) {
      setOpenPopup(true);
      setPopupTitle('포지션 이름을 입력하세요.');
      setPopupButtonText('');
      setCustomFunction('');
      return;
    } else if (skills.length === 0) {
      setOpenPopup(true);
      setPopupTitle('스킬을 추가 및 입력해주세요.');
      setPopupButtonText('');
      setCustomFunction('');
      return;
    } else if (emptySkill) {
      setOpenPopup(true);
      setPopupTitle(`${ targetIndex }번째 스킬명을 입력하세요.`);
      setPopupButtonText('');
      setCustomFunction('');
      return;
    }

    try {
      if (isSubmitting.current) return; // 실행 중일 경우 중복 클릭 방지
      isSubmitting.current = true;      // 버튼 비활성화
      setButtonLoading(true);           // 버튼 로딩 표시

      // 1. 사용자 포지션 문서 업데이트 (merge:true로 기존 필드 유지)
      const posDocRef = doc(db, "users_data", userID, "positions", positionName);
      await setDoc(posDocRef, { updatedAt: new Date() }, { merge: true });
      
      // 2. 사용자 포지션의 skills 하위컬렉션 업데이트
      const skillsRef = collection(db, "users_data", userID, "positions", positionName, "skills");
      const existingSkillsSnapshot = await getDocs(skillsRef);
      await Promise.all(existingSkillsSnapshot.docs.map((docSnap) => deleteDoc(docSnap.ref)));
      
      await Promise.all(
        skills.map((skill) =>
          addDoc(skillsRef, {
            name: skill.name,
            icon: skill.icon,
            level: skill.level,
            link: skill.link,
          })
        )
      );
      
      // 3. act 필드를 확인하여, 이미 뮤지카드로 내보낸 경우 뮤지카드의 포지션 문서도 업데이트
      const posDocSnap = await getDoc(posDocRef);
      if (posDocSnap.exists()) {
        const posData = posDocSnap.data();
        if (posData.act) {
          // act 객체의 키가 뮤지카드 ID라고 가정하고, 값이 해당 포지션명(positionName)이라면 업데이트 진행
          for (const musicianCardId in posData.act) {
            if (posData.act[musicianCardId] === positionName) {
              // 3-1. 뮤지카드의 포지션 문서 업데이트
              const musicianPosRef = doc(db, "musicians", musicianCardId, "positions", positionName);
              await setDoc(musicianPosRef, { updatedAt: new Date() }, { merge: true });
              
              // 3-2. 뮤지카드의 해당 포지션 하위 스킬 업데이트
              const musicianSkillsRef = collection(db, "musicians", musicianCardId, "positions", positionName, "skills");
              const existingMusicianSkillsSnapshot = await getDocs(musicianSkillsRef);
              await Promise.all(existingMusicianSkillsSnapshot.docs.map((docSnap) => deleteDoc(docSnap.ref)));
              await Promise.all(
                skills.map((skill) =>
                  addDoc(musicianSkillsRef, {
                    name: skill.name,
                    icon: skill.icon,
                    level: skill.level,
                    link: skill.link,
                  })
                )
              );
              const changedPaths = [musicianPosRef.path, musicianSkillsRef.path];
              await saveUserLog('musicard_logs', musicianCardId, 'update', changedPaths, 'PositionsDetail');
            }
          }
        }
      }

      isSubmitting.current = false;   // 버튼 활성화
      setButtonLoading(false);        // 버튼 로딩 숨김

      setOpenPopup(true);
      setPopupTitle('Position & Skill이 저장되었습니다.');
      setPopupButtonText('목록으로 이동');
      setCustomFunction(() => () => navigate("/my-page/position-skill"));

      // 추가일 경우 필드 초기화
      if (positionId === 'new') {
        setPositionName('');
        setSkills([]);
      }
    } catch (error) {
      isSubmitting.current = false;   // 버튼 활성화
      setButtonLoading(false);
      console.error("Position & Skill 업데이트 오류:", error);
      
      // alert("저장 중 오류가 발생했습니다.");
      setOpenPopup(true);
      setPopupTitle("Position & Skill 업데이트 중 오류가 발생했습니다.");
      setPopupButtonText('');
      setCustomFunction('');
    }
    setIsLoading(false);
  };  

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/login', { state: { from: window.location.pathname } });
      } else {
        setUserID(user.uid);
        fetchPositions();
      }
    });
    return () => unsubscribe();
  }, [navigate, fetchPositions])

  return (
    <>
      <SEO title={`포지션 & 스킬 ${ positionId === 'new' ? '추가' : '수정' }`} />
      <div className={styleClasses.myPage}>
        <div className={styleClasses.inner}>
          <div className={styleClasses.title}>
            <h1>Position & Skill {positionId === "new" ? "(새 포지션 추가)" : ""}</h1>
          </div>
          <div className={styleClasses.editContent}>
            <div className={styleClasses.leftContent}>
              <MyPageMenu currentPage='position-skill' />
            </div>
              <div className={ styleClasses.rightContent }>
                <div className={ styleClasses.content }>
                  <div className={ styleClasses.positionSkillDetail }>
                    {
                      isLoading
                      ?
                      <Loading />
                      :
                      <>
                        <div className={ styleClasses.detailBox }>
                          <div className="commonForm">
                            <div className="formRow">
                              <div className="labelBox">
                                <label>포지션</label>
                                <div className={`commonIcon toolTip ${ styleClasses.topButtonToolTip }`}>
                                  <i className="questionBlack w16"></i>
                                  <div className="toolTipText bottom">
                                    <p>Creator : 창작자 (작곡/작사/비트메이킹 등) <br />Player : 퍼포머 (보컬/피아노/댄스 등) <br />Engineer : 엔지니어 (믹싱/마스터링/사운드 디자인 등)</p>
                                  </div>
                                </div>
                              </div>
                              <div className="fieldBox checkBox">
                              {
                                positionId === "new"
                                ?
                                ["creator", "player", "engineer"].map((pos) => (
                                  <div className="eachBox" key={ pos }>
                                    <input type="radio" name="position" id={`position-${ pos }`} value={pos} checked={positionName === pos} onChange={(e) => setPositionName(e.target.value)} />
                                    <label htmlFor={`position-${ pos }`}>{pos.charAt(0).toUpperCase() + pos.slice(1)}</label>
                                  </div>
                                ))
                                :
                                (
                                  <input type="text" value={positionName} disabled />
                                )
                              }
                              </div>
                            </div>              
                            <div className="formRow">
                              <div className="labelBox">
                                <label>스킬 목록</label>
                                <div className={`commonIcon toolTip ${ styleClasses.topButtonToolTip }`}>
                                  <i className="questionBlack w16"></i>
                                  <div className="toolTipText bottom">
                                    <p><strong>스킬명</strong><br />스킬 이미지 (활동사진 등) <br /><br /><strong>스킬 레벨</strong><br />클릭 시 이동할 링크 <br />(스킬을 보여줄 음원 및 활동 링크 등)</p>
                                  </div>
                                </div>
                              </div>
                              {/* <div className={`fieldBox ${ styleClasses.innerBox }`}> */}
                              {skills.map((skill, index) => (
                                <div key={skill.id ? `skill-${skill.id}` : `skill-${index}`} className={`fieldBox ${ styleClasses.innerBox }`}>
                                  <div className={ styleClasses.eachField }>
                                    <input
                                      type="text"
                                      value={skill.name}
                                      onChange={(e) => handleSkillChange(index, "name", e.target.value)}
                                      placeholder="스킬명"
                                    />
                                  </div>
                                  <div className={ styleClasses.eachField }>
                                    <input
                                      type="text"
                                      value={skill.icon}
                                      onChange={(e) => handleSkillChange(index, "icon", e.target.value)}
                                      placeholder="이미지 URL"
                                    />
                                    <div className={`commonButton ${ styleClasses.buttons }`}>
                                      <label className={styleClasses.uploadLabel}>
                                        이미지 업로드
                                        <div className={`buttonLoading black ${ uploadingSkillIndex === index ? 'active' : undefined }`}>
                                          <div></div>
                                          <div></div>
                                          <div></div>
                                        </div>
                                        <input  type="file"  accept="image/*"  style={{ display: "none" }}  onChange={(e) => handleSkillFileUpload(e, index)}  />
                                      </label>
                                    </div>
                                  {/* {uploadingSkillIndex === index && <span style={{ marginLeft: "8px" }}>업로드 중...</span>} */}
                                  </div>
                                  <div className={ styleClasses.eachField }>
                                    <select
                                      value={skill.level}
                                      onChange={(e) => handleSkillChange(index, "level", e.target.value)}
                                    >
                                      <option value="Beginner">Beginner</option>
                                      <option value="Intermediate">Intermediate</option>
                                      <option value="Advanced">Advanced</option>
                                      <option value="Expert">Expert</option>
                                    </select>
                                  </div>
                                  <div className={ styleClasses.eachField }>
                                    <input
                                      type="text"
                                      value={skill.link}
                                      onChange={(e) => handleSkillChange(index, "link", e.target.value)}
                                      placeholder="관련 링크"
                                    />
                                  </div>
                                  {/* 소스 가져오기 버튼 */}
                                  <div className={`commonButton ${ styleClasses.buttons }`}>
                                    {/* <button type="button" onClick={ () => navigate(`/my-page/position-skill/${positionId}/source-selector`, { state: { skillIndex: index } }) }> */}
                                    <button type="button" onClick={ () => clickRetrieveAlbum(index) }>
                                      링크 가져오기
                                    </button>
                                    <button type="button" className="secondary" onClick={() => removeSkill(index)}>
                                      삭제
                                    </button>
                                  </div>
                                </div>
                              ))}
                              {/* </div> */}
                              <div className={`commonButton ${ styleClasses.outerButton }`}>
                                <button type="button" onClick={ addSkill }>
                                  <div className="commonIcon">
                                    <i className="plusBlack w24"></i>
                                  </div>
                                  스킬 추가
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className={ styleClasses.buttonWrap }>
                            <div className={ styleClasses.topButtons }>
                            <div className="commonButton fullwidth">
                                <Link to={`/my-page/position-skill/`} className='secondary'>목록으로 이동</Link>
                            </div>
                            <div className="commonButton fullwidth">
                                <button type="button" className="black" onClick={handleSave}>
                                  저장하기
                                  <div className={`buttonLoading ${ buttonLoading ? 'active' : undefined }`}>
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                  </div>
                                </button>
                            </div>
                          </div>
                        </div>
                      </>
                    }
                  </div>
                </div>
              </div>
          </div>
        </div>
      </div>
      <MusicardSourcePopup
        open={ openSourcePopup }
        setOpenPopup={ setOpenSourcePopup }
        handleTrackSelect={ handleTrackSelect }
        handleActivitySelect={ handleActivitySelect }
      />
      <Popup
        open={ openPopup }
        setOpenPopup={ setOpenPopup }
        title={ popupTitle }
        buttonText={ popupButtonText }
        buttonFunction={ customFunction }
      />
    </>
  );
}

export default PositionsDetail;